/**
 * WorkflowStepBasics (Step 1)
 *
 * Job snapshot & jurisdiction context confirmation.
 * Substeps: address, jurisdiction, propertyType, valuation, contractorRole
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { MapPin, Building2, User, DollarSign, Check, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useJurisdictionDetail } from '@/hooks/useJurisdictions';
import { getNocThreshold } from '@/lib/notice-of-commencement';
import type { UseJobWorkflowReturn } from '@/hooks/useJobWorkflow';
import type { JobData } from '../WorkflowContainer';
import { CONTRACTOR_ROLES, type ContractorRole } from '@/types/wizard';

// Required fields and their weights for data completeness
const REQUIRED_FIELDS = [
  { key: 'title', label: 'Job title', weight: 15 },
  { key: 'location', label: 'Property address', weight: 20 },
  { key: 'jurisdictionId', label: 'Jurisdiction', weight: 20 },
  { key: 'permitType', label: 'Permit type', weight: 15 },
  { key: 'client_name', label: 'Client name', weight: 10 },
  { key: 'propertyType', label: 'Property type', weight: 10 },
  { key: 'contractorRole', label: 'Contractor role', weight: 10 },
] as const;

function calculateDataCompleteness(jobData: JobData): number {
  let score = 0;
  REQUIRED_FIELDS.forEach(({ key, weight }) => {
    if (jobData[key]) {
      score += weight;
    }
  });
  return Math.min(100, score);
}

interface WorkflowStepBasicsProps {
  jobData: JobData;
  workflow: UseJobWorkflowReturn;
  onUpdate?: (updates: Partial<JobData>) => void;
}

const PROPERTY_TYPES = [
  { value: 'residential', label: 'Residential', description: 'Single-family, multi-family, townhouse' },
  { value: 'commercial', label: 'Commercial', description: 'Office, retail, restaurant, warehouse' },
];

export function WorkflowStepBasics({ jobData, workflow, onUpdate }: WorkflowStepBasicsProps) {
  const currentSubstep = workflow.currentSubstepId;
  const [editingPropertyType, setEditingPropertyType] = useState(false);
  const [editingRole, setEditingRole] = useState(false);
  const { data: jurisdictionDetail } = useJurisdictionDetail(jobData.jurisdictionId ?? null);
  const nocThreshold = getNocThreshold(
    jurisdictionDetail?.notice_of_commencement,
    [jobData.permitType || '', jobData.trade || ''].filter(Boolean)
  );
  const nocThresholdAmount = nocThreshold?.amount ?? 2500;

  // Calculate data completeness
  const dataCompleteness = useMemo(() => calculateDataCompleteness(jobData), [jobData]);

  // Refs to prevent re-render cascades (workflow object is new on every render)
  const lastProgressRef = useRef<number>(-1);
  const lastSubstepsRef = useRef<string>('');

  // Update workflow progress and mark substeps as complete
  useEffect(() => {
    // Build a key of which substeps should be complete
    const substepKey = [
      jobData.location ? 'address' : '',
      jobData.jurisdictionId ? 'jurisdiction' : '',
      jobData.propertyType ? 'propertyType' : '',
      jobData.contractorRole ? 'contractorRole' : '',
      jobData.estimatedValue ? 'valuation' : '',
    ].join(',');

    // Only update progress if the value actually changed
    if (lastProgressRef.current !== dataCompleteness) {
      lastProgressRef.current = dataCompleteness;
      workflow.updateProgress({ dataCompletenessPct: dataCompleteness });
    }

    // Only mark substeps if the set changed
    if (lastSubstepsRef.current !== substepKey) {
      lastSubstepsRef.current = substepKey;
      if (jobData.location) workflow.markSubstepComplete('basics', 'address');
      if (jobData.jurisdictionId) workflow.markSubstepComplete('basics', 'jurisdiction');
      if (jobData.propertyType) workflow.markSubstepComplete('basics', 'propertyType');
      if (jobData.contractorRole) workflow.markSubstepComplete('basics', 'contractorRole');
      if (jobData.estimatedValue) workflow.markSubstepComplete('basics', 'valuation');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobData, dataCompleteness]);

  const handleUpdate = (field: string, value: unknown) => {
    if (onUpdate) {
      onUpdate({ [field]: value });
    }
  };

  return (
    <div className="space-y-6">
      {/* Address Confirmation */}
      <Card className={cn(currentSubstep === 'address' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Job Address <span className="text-destructive">*</span>
            {jobData.location && (
              <Badge variant="outline" className="ml-auto text-green-600 border-green-300 dark:text-green-300 dark:border-green-700">
                <Check className="h-3 w-3 mr-1" />
                Confirmed
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="font-medium">{jobData.location || 'No address specified'}</p>
              {jobData.location && (
                <Button variant="link" size="sm" className="h-auto p-0 mt-2 text-blue-400 hover:text-blue-300">
                  Edit address
                </Button>
              )}
            </div>
            {!jobData.location && (
              <div className="space-y-2">
                <Label htmlFor="location">Street Address</Label>
                <Input
                  id="location"
                  placeholder="123 Main Street, City, FL 33XXX"
                  value={jobData.location || ''}
                  onChange={(e) => handleUpdate('location', e.target.value)}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Jurisdiction Confirmation */}
      <Card className={cn(currentSubstep === 'jurisdiction' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Jurisdiction <span className="text-destructive">*</span>
            {jobData.jurisdictionId ? (
              <Badge variant="outline" className="ml-auto text-green-600 border-green-300 dark:text-green-300 dark:border-green-700">
                <Check className="h-3 w-3 mr-1" />
                Resolved
              </Badge>
            ) : (
              <Badge variant="outline" className="ml-auto text-amber-600 border-amber-300 dark:text-amber-300 dark:border-amber-700">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Lookup Required
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {jobData.jurisdictionId ? (
            <div className="space-y-3">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="font-medium">{jobData.jurisdictionAhjName || 'Jurisdiction resolved'}</p>
                {jobData.jurisdictionCode && (
                  <p className="text-sm text-muted-foreground">
                    Code: {jobData.jurisdictionCode}
                  </p>
                )}
              </div>
              {jobData.jurisdictionOverlays && (
                <div className="flex flex-wrap gap-2">
                  {jobData.jurisdictionOverlays.isHvhz && (
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                      HVHZ Zone
                    </Badge>
                  )}
                  {jobData.jurisdictionOverlays.isFloodZone && (
                    <Badge variant="secondary">Flood Zone</Badge>
                  )}
                  {jobData.jurisdictionOverlays.isCoastalZone && (
                    <Badge variant="secondary">Coastal Zone</Badge>
                  )}
                </div>
              )}
              <ConfidenceBadge level="high" label="Auto-detected from address" />
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                We need to determine which permitting authority has jurisdiction over this property.
              </p>
              <Button>
                <MapPin className="h-4 w-4 mr-2" />
                Lookup Jurisdiction
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Property Type */}
      <Card className={cn(currentSubstep === 'propertyType' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Property Type <span className="text-destructive">*</span>
            {jobData.propertyType && (
              <Badge variant="outline" className="ml-auto text-green-600 border-green-300 dark:text-green-300 dark:border-green-700">
                <Check className="h-3 w-3 mr-1" />
                Selected
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {jobData.propertyType && !editingPropertyType ? (
            <div className="space-y-3">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="font-medium capitalize">{PROPERTY_TYPES.find(t => t.value === jobData.propertyType)?.label || jobData.propertyType}</p>
                <p className="text-sm text-muted-foreground">
                  {PROPERTY_TYPES.find(t => t.value === jobData.propertyType)?.description}
                </p>
              </div>
              <ConfidenceBadge level="high" label="Set during job creation" />
              <Button variant="link" size="sm" className="h-auto p-0 text-blue-400 hover:text-blue-300" onClick={() => setEditingPropertyType(true)}>
                Change property type
              </Button>
            </div>
          ) : (
            <RadioGroup
              value={jobData.propertyType || ''}
              onValueChange={(value) => {
                handleUpdate('propertyType', value);
                setEditingPropertyType(false);
              }}
              className="grid gap-3"
            >
              {PROPERTY_TYPES.map((type) => (
                <Label
                  key={type.value}
                  htmlFor={`property-${type.value}`}
                  className={cn(
                    'flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
                    'hover:bg-muted/50',
                    jobData.propertyType === type.value && 'border-primary bg-primary/5'
                  )}
                >
                  <RadioGroupItem value={type.value} id={`property-${type.value}`} />
                  <div>
                    <span className="font-medium">{type.label}</span>
                    <p className="text-sm text-muted-foreground">{type.description}</p>
                  </div>
                </Label>
              ))}
            </RadioGroup>
          )}
        </CardContent>
      </Card>

      {/* Project Valuation (Optional) */}
      <Card className={cn(currentSubstep === 'valuation' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Project Valuation
            <Badge variant="outline" className="ml-auto text-muted-foreground">
              Optional
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Project value affects permit fees and may trigger additional requirements like
              Notice of Commencement (NOC requirements vary by jurisdiction).
            </p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                type="number"
                placeholder="0.00"
                className="pl-7"
                value={jobData.estimatedValue || ''}
                onChange={(e) =>
                  handleUpdate('estimatedValue', e.target.value ? Number(e.target.value) : undefined)
                }
              />
            </div>
            {jobData.estimatedValue && jobData.estimatedValue > nocThresholdAmount && (
              <p className="text-sm text-amber-600 dark:text-amber-300 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                Notice of Commencement will be required
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contractor Role */}
      <Card className={cn(currentSubstep === 'contractorRole' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5" />
            Your Role <span className="text-destructive">*</span>
            {jobData.contractorRole && (
              <Badge variant="outline" className="ml-auto text-green-600 border-green-300 dark:text-green-300 dark:border-green-700">
                <Check className="h-3 w-3 mr-1" />
                Selected
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {jobData.contractorRole && !editingRole ? (
            <div className="space-y-3">
              {(() => {
                const selectedRole = CONTRACTOR_ROLES.find(r => r.code === jobData.contractorRole);
                return selectedRole ? (
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="font-medium">{selectedRole.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{selectedRole.description}</p>
                    <div className="mt-2 p-2 rounded bg-muted/50 text-xs">
                      <p className="font-medium text-foreground">Permit Responsibility:</p>
                      <p className="text-muted-foreground">{selectedRole.permitResponsibility}</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="font-medium capitalize">{jobData.contractorRole}</p>
                  </div>
                );
              })()}
              <ConfidenceBadge level="high" label="Set during job creation" />
              <Button variant="link" size="sm" className="h-auto p-0 text-blue-400 hover:text-blue-300" onClick={() => setEditingRole(true)}>
                Change role
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Your role determines who is responsible for pulling permits.
              </p>
              <RadioGroup
                value={jobData.contractorRole || ''}
                onValueChange={(value) => {
                  handleUpdate('contractorRole', value as ContractorRole);
                  setEditingRole(false);
                }}
                className="grid gap-3"
              >
                {CONTRACTOR_ROLES.map((role) => (
                  <Label
                    key={role.code}
                    htmlFor={`role-${role.code}`}
                    className={cn(
                      'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
                      'hover:bg-muted/50',
                      jobData.contractorRole === role.code && 'border-primary bg-primary/5'
                    )}
                  >
                    <RadioGroupItem value={role.code} id={`role-${role.code}`} className="mt-0.5" />
                    <div className="flex-1">
                      <span className="font-medium">{role.name}</span>
                      <p className="text-sm text-muted-foreground mt-1">{role.description}</p>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
